// src/components/ExportData.tsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, FileText, FileSpreadsheet, FileJson, Check } from 'lucide-react';
import { useDevices } from '../context/DeviceContext';
import { ExportService } from '../services/ExportService';
import toast from 'react-hot-toast';

export const ExportData: React.FC = () => {
  const { t } = useTranslation();
  const { devices } = useDevices();
  const [exporting, setExporting] = useState(false);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      ExportService.exportToCSV(devices);
      toast.success(t('export.success.csv'));
    } catch (error) {
      console.error('Export error:', error);
      toast.error(t('export.error'));
    } finally {
      setExporting(false);
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      ExportService.exportToExcel(devices);
      toast.success(t('export.success.excel'));
    } catch (error) {
      console.error('Export error:', error);
      toast.error(t('export.error'));
    } finally {
      setExporting(false);
    }
  };

  const handleExportJSON = async () => {
    setExporting(true);
    try {
      ExportService.exportToJSON(devices);
      toast.success(t('export.success.json'));
    } catch (error) {
      console.error('Export error:', error);
      toast.error(t('export.error'));
    } finally {
      setExporting(false);
    }
  };

  const summary = ExportService.getExportSummary(devices);

  return (
    <div
      style={{
        background: 'white',
        borderRadius: '16px',
        padding: '2rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '1.5rem',
          paddingBottom: '1rem',
          borderBottom: '2px solid #F3F4F6'
        }}
      >
        <Download size={24} color="#FF6B35" />
        <h2
          style={{
            fontSize: '1.5rem',
            fontWeight: 700,
            color: '#3f3f3f',
            margin: 0
          }}
        >
          {t('export.title')}
        </h2>
      </div>

      <p
        style={{
          color: '#6B7280',
          marginBottom: '1.5rem',
          lineHeight: 1.6
        }}
      >
        {t('export.description')}
      </p>

      {/* Export Summary */}
      <div
        style={{
          background: 'linear-gradient(135deg, rgba(255, 108, 53, 0.05) 0%, rgba(255, 195, 61, 0.05) 100%)',
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '1.5rem',
          border: '1px solid #FEF3C7'
        }}
      >
        <h3
          style={{
            fontSize: '0.875rem',
            fontWeight: 600,
            color: '#3f3f3f',
            marginBottom: '1rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}
        >
          {t('export.summary.title')}
        </h3>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '1rem'
          }}
        >
          <div>
            <p style={{ fontSize: '0.875rem', color: '#6B7280', margin: 0 }}>
              {t('export.summary.totalDevices')}
            </p>
            <p
              style={{
                fontSize: '1.5rem',
                fontWeight: 700,
                color: '#FF6B35',
                margin: '0.25rem 0 0 0'
              }}
            >
              {summary.totalDevices}
            </p>
          </div>
          <div>
            <p style={{ fontSize: '0.875rem', color: '#6B7280', margin: 0 }}>
              {t('export.summary.avgCharge')}
            </p>
            <p
              style={{
                fontSize: '1.5rem',
                fontWeight: 700,
                color: '#10B981',
                margin: '0.25rem 0 0 0'
              }}
            >
              {summary.averageCharge}%
            </p>
          </div>
          <div>
            <p style={{ fontSize: '0.875rem', color: '#6B7280', margin: 0 }}>
              {t('export.summary.avgHealth')}
            </p>
            <p
              style={{
                fontSize: '1.5rem',
                fontWeight: 700,
                color: '#3B82F6',
                margin: '0.25rem 0 0 0'
              }}
            >
              {summary.averageHealth}%
            </p>
          </div>
        </div>
      </div>

      {/* Export Buttons */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem'
        }}
      >
        <button
          onClick={handleExportCSV}
          disabled={exporting || devices.length === 0}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            padding: '1rem',
            background: 'linear-gradient(135deg, #FF6B35 0%, #FFD23F 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontWeight: 600,
            fontSize: '1rem',
            cursor: devices.length === 0 ? 'not-allowed' : 'pointer',
            opacity: devices.length === 0 ? 0.5 : 1,
            transition: 'all 0.3s',
            fontFamily: 'inherit'
          }}
          onMouseOver={e => {
            if (devices.length > 0) {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 10px 25px rgba(255, 108, 53, 0.2)';
            }
          }}
          onMouseOut={e => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <FileText size={20} />
          <span>{t('export.formats.csv')}</span>
        </button>

        <button
          onClick={handleExportExcel}
          disabled={exporting || devices.length === 0}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            padding: '1rem',
            background: '#10B981',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontWeight: 600,
            fontSize: '1rem',
            cursor: devices.length === 0 ? 'not-allowed' : 'pointer',
            opacity: devices.length === 0 ? 0.5 : 1,
            transition: 'all 0.3s',
            fontFamily: 'inherit'
          }}
          onMouseOver={e => {
            if (devices.length > 0) {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 10px 25px rgba(16, 185, 129, 0.2)';
            }
          }}
          onMouseOut={e => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <FileSpreadsheet size={20} />
          <span>{t('export.formats.excel')}</span>
        </button>

        <button
          onClick={handleExportJSON}
          disabled={exporting || devices.length === 0}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            padding: '1rem',
            background: '#3B82F6',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontWeight: 600,
            fontSize: '1rem',
            cursor: devices.length === 0 ? 'not-allowed' : 'pointer',
            opacity: devices.length === 0 ? 0.5 : 1,
            transition: 'all 0.3s',
            fontFamily: 'inherit'
          }}
          onMouseOver={e => {
            if (devices.length > 0) {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 10px 25px rgba(59, 130, 246, 0.2)';
            }
          }}
          onMouseOut={e => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <FileJson size={20} />
          <span>{t('export.formats.json')}</span>
        </button>
      </div>

      {devices.length === 0 && (
        <p
          style={{
            marginTop: '1rem',
            fontSize: '0.875rem',
            color: '#EF4444',
            textAlign: 'center'
          }}
        >
          {t('export.noDevices')}
        </p>
      )}
    </div>
  );
};
